import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Row, Col, Panel } from 'react-bootstrap';
import { loadSalesAnalyticsData } from './actions';
import Filter from './Filter';
import moment from 'moment';
import { loadUnpagedTerms, getDefaultActiveTerm, getLastTerm } from '../../terms/actions';
import authUtils from '../../../auth/utils';
import Stats from './Stats';
import LessonPaymentsRevenue from './LessonPaymentsRevenue';
import LessonPaymentsGrowthRate from './LessonPaymentsGrowthRate';
import LessonPaymentsCumulativeRevenue from './LessonPaymentsCumulativeRevenue';
import LessonPaymentsByGrade from './LessonPaymentsByGrade';
import LessonPaymentsByCenter from './LessonPaymentsByCenter';
import LessonPaymentsByPaymentType from './LessonPaymentsByPaymentType';
import LessonPaymentsByPaymentMethod from './LessonPaymentsByPaymentMethod';
import Discounts from './Discounts';
import DiscountsByType from './DiscountsByType';
import ReferralSource from './ReferralSource';
import UncompletedSalesStats from './UncompletedSalesStats';
import { withTranslation } from 'react-i18next';
import globalize from '../../../common/services/globalize';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChartColumn } from '@fortawesome/free-solid-svg-icons';
import { renderExportButton } from '../utils';

class SalesDashboard extends Component {
	constructor(props) {
		super(props);
		this.handleFilterApply = this.handleFilterApply.bind(this);
		this.state = {
			...this.getDefaultFilter(props.franchisor)
		};
	}

	componentDidMount() {
		const onTermsLoaded = () => {
			const { activeTermId, activeSchoolYearId } = this.props;

			if (activeTermId && activeSchoolYearId) {
				this.setState({
					termId: activeTermId,
					schoolYearId: activeSchoolYearId
				}, () => this.reloadData());
			}
		};

		this.props.loadUnpagedTerms(null, onTermsLoaded, onTermsLoaded);
	}

	getDefaultFilter() {
		const { franchisor, activeTermId, activeSchoolYearId } = this.props;

		const today = moment().startOf('date');
		return {
			periodFrom: today.clone().subtract(1, 'month'),	// periodFrom and periodTo will be used if active term is not loaded yet
			periodTo: today,
			termId: activeTermId,							// termId and schoolYearId will be used if active term is already loaded
			schoolYearId: activeSchoolYearId,
			centerIds: null,
			selectedCategories: franchisor && (franchisor.offerIndividualConsultations || franchisor.offerWorkshops) ? [] : [1]	// franchisor should be already loaded
			// load Lessons only if current franchisor doesn't have Consultations or Workshops
		};
	};

	reloadData() {
		const { periodFrom, periodTo, centerIds, termId, schoolYearId, selectedCategories } = this.state;

		const statsRequestData = {
			periodFrom: periodFrom.startOf('day').format(),
			periodTo: periodTo.endOf('day').format(),
			termId,
			schoolYearId,
			centerIds: centerIds || [],
			paymentCategories: termId || schoolYearId ? [] : selectedCategories,//send selected categories only if period is selected 
			timeZone: globalize.getCurrentTimeZone(this.props.franchisor)
		};

		const { loadSalesAnalyticsData, franchisor } = this.props;
		loadSalesAnalyticsData('dashboardStats', statsRequestData);
		loadSalesAnalyticsData('lessonPaymentsRevenue', statsRequestData);
		loadSalesAnalyticsData('lessonPaymentsByCategory', statsRequestData);
		loadSalesAnalyticsData('lessonPaymentsGrowthRate', statsRequestData);
		loadSalesAnalyticsData('uncompletedSalesStats', statsRequestData);

		const showCollectedRevenue = franchisor && franchisor.enableInstalmentPayment && !termId && !schoolYearId;
		if (showCollectedRevenue) {
			loadSalesAnalyticsData('collectedRevenueStats', statsRequestData);
		}
	}

	handleFilterApply(filter) {
		this.setState({ ...filter }, () => this.reloadData());
	}

	render() {
		const {
			currentUser,
			terms,
			schoolYears,
			paymentCategories,
			analytics: {
				dashboardStats,
				lessonPaymentsRevenue,
				lessonPaymentsByCategory = {
					loading: false, data: null
				},
				lessonPaymentsGrowthRate,
				uncompletedSalesStats,
				collectedRevenueStats
			},
			franchisor,
			t
		} = this.props;

		const showCollectedRevenue = franchisor && franchisor.enableInstalmentPayment && !this.state.termId && !this.state.schoolYearId;
		const allDashboardStats = {
			loading: (dashboardStats && dashboardStats.loading) || (showCollectedRevenue && collectedRevenueStats && collectedRevenueStats.loading),
			data: { ...dashboardStats && dashboardStats.data, ...(showCollectedRevenue && collectedRevenueStats && collectedRevenueStats.data) }
		};

		const lessonPaymentsByPaymentType = {
			loading: lessonPaymentsByCategory.loading,
			data: lessonPaymentsByCategory.data && lessonPaymentsByCategory.data.paymentTypes
		};
		const lessonPaymentsByPaymentMethod = {
			loading: lessonPaymentsByCategory.loading,
			data: lessonPaymentsByCategory.data && lessonPaymentsByCategory.data.paymentMethods
		};

		const lessonPaymentsByGrade = {
			loading: lessonPaymentsByCategory.loading,
			data: lessonPaymentsByCategory.data && lessonPaymentsByCategory.data.grades
		};

		const discountsByType = {
			loading: lessonPaymentsByCategory.loading,
			data: lessonPaymentsByCategory.data && lessonPaymentsByCategory.data.discountTypes
		};

		const referralSourcesByType = {
			loading: lessonPaymentsByCategory.loading,
			data: lessonPaymentsByCategory.data && lessonPaymentsByCategory.data.referralSourceTypes
		};

		const lessonPaymentsByCenter = {
			loading: lessonPaymentsByCategory.loading,
			data: lessonPaymentsByCategory.data && lessonPaymentsByCategory.data.centers
		};

		const canViewSalesReports = authUtils.hasPermission(currentUser, 'SalesDashboard');

		const termsReferenceLines = Object.values(terms).map(x => ({ /*label: x.typeName,*/ value: x.startDate }));

		return <div>
			{canViewSalesReports && <div className='dashboard'>
				<Row className='mb-1'>
					<Col xs={12}>
						<Filter currentCulture={globalize.getCurrentCulture(franchisor)} defaultFilterValues={this.getDefaultFilter()}
							periodFrom={this.state.periodFrom} periodTo={this.state.periodTo}
							termId={this.state.termId} terms={terms}
							centerIds={this.state.centerIds}
							schoolYearId={this.state.schoolYearId} schoolYears={schoolYears} activeSchoolYearId={this.props.activeSchoolYearId}
							showPaymentCategories={franchisor.offerIndividualConsultations || franchisor.offerWorkshops} selectedCategories={this.state.selectedCategories} paymentCategories={paymentCategories}
							onApplyFilter={this.handleFilterApply}
						/>
					</Col>
				</Row>
				<Stats {...allDashboardStats} termId={this.state.termId} schoolYearId={this.state.schoolYearId} />
				<Row>
					<Col sm={12} md={6}>
						<Panel>
							<Panel.Heading><FontAwesomeIcon icon={faChartColumn} />{t('SalesDashboard_SalesForPeriod')}
								{lessonPaymentsRevenue && lessonPaymentsRevenue.data && renderExportButton(lessonPaymentsRevenue.data, 'SalesForPeriod.csv', t)}
							</Panel.Heading>
							<Panel.Body>
								<LessonPaymentsRevenue {...lessonPaymentsRevenue} referenceLinesX={termsReferenceLines} />
							</Panel.Body>
						</Panel>
					</Col>
					<Col sm={12} md={6}>
						<Panel>
							<Panel.Heading><FontAwesomeIcon icon={faChartColumn} />{t('SalesDashboard_CumulativeSalesForPeriod')}
								{lessonPaymentsRevenue && lessonPaymentsRevenue.data && renderExportButton(lessonPaymentsRevenue.data, 'CumulativeSalesForPeriod.csv', t)}
							</Panel.Heading>
							<Panel.Body>
								<LessonPaymentsCumulativeRevenue {...lessonPaymentsRevenue} referenceLinesX={termsReferenceLines} />
							</Panel.Body>
						</Panel>
					</Col>
				</Row>
				<Row>
					<Col sm={6} md={3}>
						<Panel>
							<Panel.Heading><FontAwesomeIcon icon={faChartColumn} />{t('SalesDashboard_SalesByPaymentType')}
								{lessonPaymentsByPaymentType && lessonPaymentsByPaymentType.data && renderExportButton(lessonPaymentsByPaymentType.data, 'SalesByPaymentType.csv', t)}
							</Panel.Heading>
							<Panel.Body>
								<LessonPaymentsByPaymentType {...lessonPaymentsByPaymentType} />
							</Panel.Body>
						</Panel>
					</Col>
					<Col sm={6} md={3}>
						<Panel>
							<Panel.Heading><FontAwesomeIcon icon={faChartColumn} />{t('SalesDashboard_SalesByPaymentMethod')}
								{lessonPaymentsByPaymentMethod && lessonPaymentsByPaymentMethod.data && renderExportButton(lessonPaymentsByPaymentMethod.data, 'SalesByPaymentMethod.csv', t)}
							</Panel.Heading>
							<Panel.Body>
								<LessonPaymentsByPaymentMethod {...lessonPaymentsByPaymentMethod} />
							</Panel.Body>
						</Panel>
					</Col>
					<Col sm={12} md={6}>
						<Panel>
							<Panel.Heading><FontAwesomeIcon icon={faChartColumn} />{t('SalesDashboard_SalesByGrade')}
								{lessonPaymentsByGrade && lessonPaymentsByGrade.data && renderExportButton(lessonPaymentsByGrade.data, 'SalesByGrade.csv', t)}
							</Panel.Heading>
							<Panel.Body>
								<LessonPaymentsByGrade {...lessonPaymentsByGrade} />
							</Panel.Body>
						</Panel>
					</Col>
				</Row>
				<Row>
					<Col sm={12} md={6}>
						<Panel>
							<Panel.Heading><FontAwesomeIcon icon={faChartColumn} />{t('SalesDashboard_DiscountsForPeriod')}
								{lessonPaymentsRevenue && lessonPaymentsRevenue.data && renderExportButton(lessonPaymentsRevenue.data, 'DiscountsForPeriod.csv', t)}
							</Panel.Heading>
							<Panel.Body>
								<Discounts {...lessonPaymentsRevenue} referenceLinesX={termsReferenceLines} />
							</Panel.Body>
						</Panel>
					</Col>
					<Col sm={6} md={3}>
						<Panel>
							<Panel.Heading><FontAwesomeIcon icon={faChartColumn} />{t('SalesDashboard_DiscountsByType')}
								{discountsByType && discountsByType.data && renderExportButton(discountsByType.data, 'DiscountsByType.csv', t)}
							</Panel.Heading>
							<Panel.Body>
								<DiscountsByType {...discountsByType} />
							</Panel.Body>
						</Panel>
					</Col>
					<Col sm={6} md={3}>
						<Panel>
							<Panel.Heading><FontAwesomeIcon icon={faChartColumn} />{t('SalesDashboard_ReferralSources')}
								{referralSourcesByType && referralSourcesByType.data && renderExportButton(referralSourcesByType.data, 'ReferralSources.csv', t)}
							</Panel.Heading>
							<Panel.Body>
								<ReferralSource {...referralSourcesByType} />
							</Panel.Body>
						</Panel>
					</Col>
				</Row>
				<Row>
					<Col sm={12} md={6}>
						<Panel>
							<Panel.Heading><FontAwesomeIcon icon={faChartColumn} />{t('SalesDashboard_GrowthRate')}
								{lessonPaymentsGrowthRate && lessonPaymentsGrowthRate.data && renderExportButton(lessonPaymentsGrowthRate.data, 'GrowthRate.csv', t)}
							</Panel.Heading>
							<Panel.Body>
								<LessonPaymentsGrowthRate {...lessonPaymentsGrowthRate} referenceLinesX={termsReferenceLines} />
							</Panel.Body>
						</Panel>
					</Col>
					<Col sm={12} md={6}>
						<Panel>
							<Panel.Heading><FontAwesomeIcon icon={faChartColumn} />{t('SalesDashboard_SalesByCenter')}
								{lessonPaymentsByCenter && lessonPaymentsByCenter.data && renderExportButton(lessonPaymentsByCenter.data, 'SalesByCenter.csv', t)}
							</Panel.Heading>
							<Panel.Body>
								<LessonPaymentsByCenter {...lessonPaymentsByCenter} />
							</Panel.Body>
						</Panel>
					</Col>
				</Row>
				<Row>
					<Col sm={6} md={3}>
						<Panel>
							<Panel.Heading><FontAwesomeIcon icon={faChartColumn} />{t('SalesDashboard_UncompletedSales')}</Panel.Heading>
							<Panel.Body>
								<UncompletedSalesStats {...uncompletedSalesStats} />
							</Panel.Body>
						</Panel>
					</Col>
				</Row>

			</div>}
		</div>;
	}
}

const mapStateToProps = (state) => {
	const {
		analytics: { sales },
		auth: { currentUser },
		entities: { terms },
		lookups: { schoolYears, paymentCategories },
		configuration: { franchisor }
	} = state;

	let activeTerm = getDefaultActiveTerm(terms);
	if (!activeTerm) {
		activeTerm = getLastTerm(terms);
	}

	return {
		analytics: sales,
		currentUser,
		schoolYears,
		terms,
		paymentCategories,
		activeSchoolYearId: activeTerm && activeTerm.schoolYearId,
		activeTermId: activeTerm && activeTerm.id,
		franchisor
	};
};

export default connect(mapStateToProps, {
	loadSalesAnalyticsData,
	loadUnpagedTerms
})(withTranslation()(SalesDashboard));